import { t } from 'i18next';
import { ReactElement, useState } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { DropdownSingleSelect } from '../../components/Dropdown/DropdownVariants/DropdownSingleSelect';
import { DropdownOptionProps } from '../../components/Dropdown/helper/DropdownTypes';
import { Loading } from '../../components/Loading';
import { Modal } from '../../components/Modal';
import { usePrincipals } from '../../hooks/queries';
import {
  NO_PRINCIPAL_DEFINED_ERROR,
  redirectToSelectedPrincipal,
} from '../../lib/currentPrincipal';
import { storePrincipalIdSlugLocalStorage } from '../../utils/localStorage.utils';
import { Principal } from '../../utils/types';

const redirectAndStorePrincipal = (
  userId: string,
  selectedPrincipalDropdownSelection: string,
  principals: Principal[],
) => {
  storePrincipalIdSlugLocalStorage(selectedPrincipalDropdownSelection);
  redirectToSelectedPrincipal(userId, principals);
};

type PrincipalFallbackProps = FallbackProps & {
  userId: string;
};

export const ErrorFallbackPrincipals = ({
  error,
  userId,
}: PrincipalFallbackProps): ReactElement => {
  //Handle only NO_PRINCIPAL_DEFINED_ERROR error, all other errors just pass
  if (error.message !== NO_PRINCIPAL_DEFINED_ERROR) throw error;
  const { data: principals } = usePrincipals();
  const [
    selectedPrincipalDropdownSelection,
    setSelectedPrincipalDropdownSelection,
  ] = useState<string>();

  if (!principals) return <Loading loading />;

  const onModalConfirmHandler = () => {
    if (selectedPrincipalDropdownSelection) {
      redirectAndStorePrincipal(
        userId,
        selectedPrincipalDropdownSelection,
        principals,
      );
    }
  };

  //If only one principal is assigned to current user, set and store principal as current principal and redirect to it
  if (principals.length === 1) {
    setSelectedPrincipalDropdownSelection(principals[0].slug);
    onModalConfirmHandler();
  }

  const onPrincipalChangeHandler = (
    selectedOption: DropdownOptionProps | undefined,
  ) => {
    const principalSlug = (selectedOption as DropdownOptionProps)
      ?.value as string;
    if (principalSlug) setSelectedPrincipalDropdownSelection(principalSlug);
  };

  return (
    <Modal
      icon="AccountBehalf"
      heading={t('modals:changeCustomerSelection:prompt')}
      confirmText={t('modals:changeCustomerSelection:confirm')}
      onConfirm={onModalConfirmHandler}
      isHidden={false}
      isCancelButtonHidden={true}
      isCloseIconHidden={true}
    >
      <DropdownSingleSelect
        height="50vh--90"
        options={principals
          .map(({ name, slug }) => ({ name, value: slug }))
          .sort((a, b) => a.name.localeCompare(b.name))}
        onChange={onPrincipalChangeHandler}
        widthVariant="full-width"
      />
    </Modal>
  );
};
